import { FrequencyRecord, milliseconds, Note, PlayerNote, Section, Song, SongTrack } from '~/interfaces';
import { PlayerNumber } from '~/modules/players/player-number';
import isNotesSection from '~/modules/songs/utils/is-notes-section';

export const pitchPadding = 6;

export const NOTE_HEIGHT = 30;
export const BIG_NOTE_HEIGHT = NOTE_HEIGHT + 6;

/** The share of the width right of the lanes, kept clear of notes so the score column never covers them. */
export const SCORE_GUTTER_SHARE = 0.2;

export const getPlayerCanvas = (
  // `canvas` is only measured, so it is typed as its measurements - that lets callers that only
  // know the size (the DOM lane decorations) share this geometry instead of restating it.
  drawingData: Pick<DrawingData, 'playerCount' | 'playerIndex' | 'paddingVertical'> & {
    canvas: { width: number; height: number };
  },
) => {
  const width = drawingData.canvas.width;
  const height = drawingData.canvas.height;

  // If the ratio is higher than 16/9 (e.g. 21/9), pad the excess horizontal space
  // so in the end the notes are drawn over the same area as it would be on 16/9
  const pixelsOverRatio = Math.max(0, (width - height * (16 / 9)) / 2);

  const paddingHorizontal = 10 + (width - 2 * pixelsOverRatio) * 0.15 + pixelsOverRatio;
  const paddingRight = (width - 2 * pixelsOverRatio) * SCORE_GUTTER_SHARE + pixelsOverRatio;

  const paddedHeight = height - drawingData.paddingVertical * 2;

  return {
    width: width - paddingHorizontal - paddingRight,
    height: paddedHeight / drawingData.playerCount,
    baseX: paddingHorizontal,
    baseY: drawingData.paddingVertical + (drawingData.playerIndex * paddedHeight) / drawingData.playerCount,
  };
};

/** The width right of the lanes that the scores sit in, in the same pixels as `canvas`. */
export const getScoreGutter = (canvas: { width: number; height: number }) => {
  const lane = getPlayerCanvas({ canvas, playerCount: 1, playerIndex: 0, paddingVertical: 0 });

  return canvas.width - lane.baseX - lane.width;
};

/** Everyone reads one shared lane, except singers on separate parts (a duet), who keep one each.
 * `leads` marks the singer who draws what the lane shares: the target notes and their effects. */
export const getLane = (players: readonly PlayerNumber[], playerNumber: PlayerNumber, separateLanes: boolean) => {
  const index = Math.max(0, players.indexOf(playerNumber));

  return separateLanes
    ? { playerIndex: index, playerCount: Math.max(1, players.length), shared: false, leads: true }
    : { playerIndex: 0, playerCount: 1, shared: players.length > 1, leads: index === 0 };
};

export default function calculateData({
  canvas,
  currentSectionIndex,
  songBeatLength,
  minPitch,
  maxPitch,
  track,
  playerIndex,
  playerCount,
  paddingVertical,
}: DrawingData) {
  const sections = track.sections;
  const currentSection = sections[currentSectionIndex];
  const nextSection = sections[currentSectionIndex + 1];

  const playerCanvas = getPlayerCanvas({ canvas, playerCount, playerIndex, paddingVertical });

  const lastNote = isNotesSection(currentSection) ? currentSection?.notes?.at(-1) : undefined;
  const sectionEndBeat = isNotesSection(currentSection)
    ? (nextSection?.start ?? lastNote!.start + lastNote!.length)
    : currentSection.end;
  const timeSectionGap = currentSection.start * songBeatLength;
  const maxTime = (sectionEndBeat - currentSection.start) * songBeatLength;

  const pitchStepHeight = (playerCanvas.height - 20 - NOTE_HEIGHT) / (maxPitch - minPitch + pitchPadding * 2);

  return {
    playerCanvas,
    currentSection,
    sectionEndBeat,
    timeSectionGap,
    maxTime,
    pitchStepHeight,
  };
}

export interface DrawingData {
  playerNumber: PlayerNumber;
  playerIndex: number;
  playerCount: number;
  song: Song;
  songBeatLength: number;
  minPitch: number;
  maxPitch: number;
  canvas: HTMLCanvasElement;
  currentTime: milliseconds;
  currentSectionIndex: number;
  currentNote: Note | undefined;
  frequencies: FrequencyRecord[];
  playerNotes: PlayerNote[];
  currentPlayerNotes: PlayerNote[];
  track: SongTrack;

  currentBeat: number;
  paddingVertical: number;
  currentSection: Section | undefined; // `undefined` can happen with `sectionShift` parameter
}
